import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Confetti from 'react-confetti';
import { spinRaffle } from '../utils/holderService';
import { useRaffle } from '../context/RaffleContext';
import './Raffle.css';
import wheelStand from '../images/wheelstand.webp';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from '../firebase';

import { formatDistanceToNow } from 'date-fns';

// Helper function to safely parse dates
const getRelativeTime = (timestamp) => {
    try {
        if (!timestamp) return 'Unknown';
        // Handle Firestore Timestamp (has _seconds)
        const date = timestamp._seconds 
            ? new Date(timestamp._seconds * 1000) 
            : new Date(timestamp);
            
        if (isNaN(date.getTime())) return 'Invalid Date';
        return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
        return 'Unknown';
    }
};

const SHRIMP_TIER = { key: 'shrimp', label: 'Cadets', min: 1, max: 20, prize: '$2 USD' };
const WHALE_TIER = { key: 'whale', label: 'Commanders', min: 21, max: Number.POSITIVE_INFINITY, prize: '$5 USD' };

const Raffle = () => {
  const { 
    holders, 
    shrimpWinner,
    whaleWinner,
    setShrimpWinner,
    setWhaleWinner,
    shrimpRecentWinners,
    whaleRecentWinners,
    legacyRecentWinners,
    nextSpinTime, 
    loading, 
    refreshData 
  } = useRaffle();

  const [timeLeft, setTimeLeft] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [shrimpRotation, setShrimpRotation] = useState(0);
  const [whaleRotation, setWhaleRotation] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const auth = getAuth(app);
    const unsub = onAuthStateChanged(auth, (user) => {
      setAdminUser(user || null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Timer logic
  useEffect(() => {
    if (!nextSpinTime) {
      setTimeLeft("No raffle scheduled");
      return;
    }

    const timer = setInterval(() => {
      const now = new Date();
      const diff = nextSpinTime - now;

      if (diff <= 0) {
        setTimeLeft("Soon...");
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [nextSpinTime]);

  const getTierHolders = (tier) => holders.filter(
    (holder) => holder.balance >= tier.min && holder.balance <= tier.max
  );

  const getEligibleHolders = (tierHolders) => tierHolders.filter((holder) => !holder.isExcluded);

  const getRotationForWinner = (wheelHolders, winnerId, currentRotation) => {
    if (!winnerId || wheelHolders.length === 0) return currentRotation;
    const totalTickets = wheelHolders.reduce((sum, h) => sum + h.balance, 0);
    if (totalTickets === 0) return currentRotation;
    let currentRotationOffset = 0;
    let winnerAngle = 0;

    for (const holder of wheelHolders) {
      const slicePercentage = (holder.balance / totalTickets) * 100;
      const sliceAngle = (slicePercentage / 100) * 360;

      if (holder.id === winnerId) {
        winnerAngle = currentRotationOffset + sliceAngle / 2;
        break;
      }
      currentRotationOffset += sliceAngle;
    }

    const currentTotalRotation = currentRotation % 360;
    const currentWinnerPosition = (winnerAngle + currentTotalRotation) % 360;

    let degreesToRotate = 90 - currentWinnerPosition;
    if (degreesToRotate < 0) {
      degreesToRotate += 360;
    }

    const spins = 5 * 360;
    return currentRotation + spins + degreesToRotate;
  };

  const buildWheelGradient = (wheelHolders) => {
    const totalTickets = wheelHolders.reduce((sum, h) => sum + h.balance, 0);
    if (totalTickets === 0) return '#111c25 0% 100%';
    const palette = [
      '#0b2c3a',
      '#0f3b52',
      '#13466a',
      '#1a4a82',
      '#2a4b8f',
      '#3a3f8a',
      '#4a3380',
      '#592f73'
    ];
    return wheelHolders.map((holder, i) => {
      const prevTickets = wheelHolders.slice(0, i).reduce((sum, h) => sum + h.balance, 0);
      const start = (prevTickets / totalTickets) * 100;
      const end = ((prevTickets + holder.balance) / totalTickets) * 100;
      const color = palette[i % palette.length];
      return `${color} ${start}% ${end}%`;
    }).join(', ');
  };

  const renderWheelLabels = (wheelHolders) => {
    const totalTickets = wheelHolders.reduce((sum, h) => sum + h.balance, 0);
    if (totalTickets === 0) return null;
    let currentRotation = 0;
    const labelRadius = viewportWidth <= 350 ? 92 : viewportWidth <= 600 ? 105 : 140;
    const labelFontSize = viewportWidth <= 350 ? '0.58rem' : viewportWidth <= 600 ? '0.62rem' : '0.7rem';
    return wheelHolders.map((holder) => {
      const slicePercentage = (holder.balance / totalTickets) * 100;
      const sliceAngle = (slicePercentage / 100) * 360;
      if (sliceAngle < 0.5) {
        currentRotation += sliceAngle;
        return null;
      }

      const rotateStyle = {
        transform: `rotate(${currentRotation + sliceAngle / 2}deg) translateY(-${labelRadius}px) rotate(-90deg)`,
        position: 'absolute',
        top: '50%',
        left: '50%',
        transformOrigin: '0 0',
        width: '1px',
        height: '1px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 5
      };

      const textStyle = {
        whiteSpace: 'nowrap',
        color: '#e7fbff',
        fontWeight: 600,
        fontSize: labelFontSize,
        textTransform: 'uppercase',
        textShadow: '0 0 6px rgba(0, 229, 255, 0.5)',
        maxWidth: 'none',
        overflow: 'visible',
        textOverflow: 'clip',
        backgroundColor: 'rgba(4, 10, 18, 0.65)',
        border: '1px solid rgba(0, 229, 255, 0.35)',
        borderRadius: '6px',
        padding: '2px 6px',
        boxShadow: '0 0 8px rgba(0, 229, 255, 0.35)'
      };

      const label = (
        <div key={holder.id} style={rotateStyle}>
            <span style={textStyle}>{holder.id}</span>
        </div>
      );

      currentRotation += sliceAngle;
      return label;
    });
  };

  const shrimpHolders = getTierHolders(SHRIMP_TIER);
  const whaleHolders = getTierHolders(WHALE_TIER);

  const handleSpin = async () => {
    if (spinning || holders.length === 0) return;
    if (!adminUser) {
      alert("Admin login required. Go to Admin page to login.");
      return;
    }
    
    setSpinning(true);
    setShowConfetti(false);
    
    try {
        const token = await adminUser.getIdToken();
        const result = await spinRaffle(token);

        const shrimpWheelHolders = getEligibleHolders(shrimpHolders);
        const whaleWheelHolders = getEligibleHolders(whaleHolders);

        const shrimpRotationNext = result?.shrimpWinner
          ? getRotationForWinner(shrimpWheelHolders, result.shrimpWinner.walletId, shrimpRotation)
          : shrimpRotation;
        const whaleRotationNext = result?.whaleWinner
          ? getRotationForWinner(whaleWheelHolders, result.whaleWinner.walletId, whaleRotation)
          : whaleRotation;

        setShrimpRotation(shrimpRotationNext);
        setWhaleRotation(whaleRotationNext);

        setTimeout(() => {
            setShrimpWinner(result?.shrimpWinner || null);
            setWhaleWinner(result?.whaleWinner || null);
            setSpinning(false);
            setShowConfetti(true);
            refreshData();
        }, 4000);
    } catch (error) {
        console.error("Spin failed:", error);
        setSpinning(false);
        alert("Failed to execute raffle. Try again.");
    }
  };

  const renderWheel = (tierHolders, rotationValue) => {
    const wheelHolders = getEligibleHolders(tierHolders);
    const wheelGradient = buildWheelGradient(wheelHolders);

    return (
      <div className="wheel-stage">
        <div className="wheel-stand wheel-stand-back">
          <img src={wheelStand} alt="" aria-hidden="true" />
        </div>
        <div className="wheel-container">
          <div className="wheel-pointer"></div>
          <div 
            className="wheel" 
            style={{ 
              transform: `rotate(${rotationValue}deg)`,
              transition: spinning ? 'transform 4s cubic-bezier(0.1, 0, 0.1, 1)' : 'none'
            }}
          >
            {wheelHolders.length > 0 && renderWheelLabels(wheelHolders)}
            
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                zIndex: 1,
                background: `conic-gradient(${wheelGradient})`,
                opacity: 0.6
            }}></div>
          </div>
          {adminUser && (
            <button 
              className="spin-btn-center" 
              onClick={handleSpin} 
              disabled={spinning}
              aria-label="Spin raffle"
              title="Spin Raffle"
            >
              {spinning ? 'Spinning…' : 'Spin Both'}
            </button>
          )}
        </div>
        <div className="wheel-stand wheel-stand-front">
          <img src={wheelStand} alt="" aria-hidden="true" />
        </div>
      </div>
    );
  };

  const renderWinningChances = (title, tierHolders) => {
    const totalEligibleTickets = tierHolders
      .reduce((sum, holder) => sum + holder.balance, 0);

    return (
      <div className="leaderboard-container">
        <h2 className="leaderboard-title">{title}</h2>
        <div className="leaderboard-header">
          <span>Rank</span>
          <span>Holder</span>
          <span>NFTs</span>
          <span>Chance</span>
        </div>
        <div className="leaderboard-list">
          {tierHolders.map((holder, index) => {
            let chance = '0.00';
            if (totalEligibleTickets > 0) {
              chance = ((holder.balance / totalEligibleTickets) * 100).toFixed(2);
            }

            return (
              <div key={holder.id} className="leaderboard-row">
                <span className="rank">#{index + 1}</span>
                <div className="holder-info">
                    <span className="wallet">
                      {holder.id}
                    </span>
                </div>
                <span className="balance">{holder.balance}</span>
                <span className="chance">{`${chance}%`}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderRecentWinners = (title, winners) => (
    <div className="leaderboard-container">
      <h2 className="leaderboard-title">{title}</h2>
      <div className="leaderboard-header" style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
          <span>Date</span>
          <span>Winner</span>
          <span>Prize</span>
      </div>
      <div className="leaderboard-list">
          {winners.length > 0 ? (
              winners.map((win, idx) => (
                  <div key={win.id || idx} className="leaderboard-row" style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                      <span style={{fontSize: '0.8rem', color: '#888'}}>
                          {getRelativeTime(win.timestamp || win.date)}
                      </span>
                      <div className="holder-info">
                          <span className="wallet">{win.walletId}</span>
                      </div>
                      <span className="balance" style={{color: '#4ecdc4'}}>{win.prize}</span>
                  </div>
              ))
          ) : (
              <div style={{padding: '20px', textAlign: 'center', color: '#666'}}>
                  No winners yet.
              </div>
          )}
      </div>
    </div>
  );

  const renderLastWinner = (title, winner) => (
    <div className="last-winner-card">
      <div className="last-winner-title">{title}</div>
      {winner ? (
        <>
          <div className="last-winner-wallet">{winner.walletId}</div>
          <div className="last-winner-meta">
            <span>{winner.prize}</span>
            <span>{getRelativeTime(winner.timestamp || winner.date)}</span>
          </div>
        </>
      ) : (
        <div className="last-winner-empty">No winners yet.</div>
      )}
    </div>
  );

  return (
    <div className="raffle-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}

      <div className="raffle-hero">
        <div className="raffle-top-bar">
            <Link to="/" className="home-link">Home</Link>
        </div>
        
        <div className="raffle-header">
          <h1 className="raffle-title">AAA Holder Raffle</h1>
        </div>

        <div className="countdown-container">
          <div className="countdown-label">Next Spin In</div>
          <div className="countdown-timer">{spinning ? "SPINNING..." : timeLeft}</div>
        </div>

        <div className="wheels-section">
          <div className="wheels-grid">
            <div className="wheel-section">
              <div className="wheel-title">Cadet Wheel</div>
              <div className="wheel-subtitle">1–20 NFTs · {SHRIMP_TIER.prize}</div>
              {renderWheel(shrimpHolders, shrimpRotation)}
            </div>
            <div className="wheel-section">
              <div className="wheel-title">Commander Wheel</div>
              <div className="wheel-subtitle">21+ NFTs · {WHALE_TIER.prize}</div>
              {renderWheel(whaleHolders, whaleRotation)}
            </div>
          </div>
          <div className="last-winners-row">
            {renderLastWinner('Last Cadet Winner', shrimpWinner)}
            {renderLastWinner('Last Commander Winner', whaleWinner)}
          </div>
        </div>
      </div>

      {showConfetti && (shrimpWinner || whaleWinner) && (
          <div className="winner-popup-overlay">
              <div className="winner-popup-content winner-popup-dual">
                  <button className="close-popup-btn" onClick={() => {
                      setShrimpWinner(null);
                      setWhaleWinner(null);
                      setShowConfetti(false);
                  }}>×</button>
                  
                  <div className="popup-trophy">🏆</div>
                  <h2>We have winners!</h2>
                  
                  <div className="popup-winners-grid">
                      <div className="popup-winner-card">
                          <div className="popup-winner-title">Cadet Winner</div>
                          {shrimpWinner ? (
                            <div className="popup-wallet-large">
                                {shrimpWinner.walletId}
                                <button 
                                  className="copy-wallet-btn" 
                                  onClick={() => navigator.clipboard.writeText(shrimpWinner.walletId)}
                                  aria-label="Copy wallet ID"
                                  title="Copy wallet ID"
                                  style={{ marginLeft: '8px' }}
                                >
                                  Copy
                                </button>
                            </div>
                          ) : (
                            <div className="popup-empty">No eligible cadets</div>
                          )}
                          {shrimpWinner?.prize && (
                            <div className="popup-prize">
                                <span>Prize:</span> {shrimpWinner.prize}
                            </div>
                          )}
                      </div>

                      <div className="popup-winner-card">
                          <div className="popup-winner-title">Commander Winner</div>
                          {whaleWinner ? (
                            <div className="popup-wallet-large">
                                {whaleWinner.walletId}
                                <button 
                                  className="copy-wallet-btn" 
                                  onClick={() => navigator.clipboard.writeText(whaleWinner.walletId)}
                                  aria-label="Copy wallet ID"
                                  title="Copy wallet ID"
                                  style={{ marginLeft: '8px' }}
                                >
                                  Copy
                                </button>
                            </div>
                          ) : (
                            <div className="popup-empty">No eligible commanders</div>
                          )}
                          {whaleWinner?.prize && (
                            <div className="popup-prize">
                                <span>Prize:</span> {whaleWinner.prize}
                            </div>
                          )}
                      </div>
                  </div>
              </div>
          </div>
      )}

      {loading && <div className="loading-spinner"></div>}

      {!loading && holders.length > 0 && (
        <>
          <div className="stats-container">
              {renderWinningChances('Cadet Winning Chances', shrimpHolders)}
              {renderWinningChances('Commander Winning Chances', whaleHolders)}
              {renderRecentWinners('Recent Cadet Winners', shrimpRecentWinners)}
              {renderRecentWinners('Recent Commander Winners', whaleRecentWinners)}
          </div>
          <div className="legacy-winners-section">
              {renderRecentWinners('Legacy Winners (Pre-Tier)', legacyRecentWinners)}
          </div>
        </>
      )}

      <div className="transparency-section">
        <p>
          <strong>Fairness & Transparency:</strong> <br/>
          Raffle logic runs on a secure backend. Data is fetched directly from the Hedera Mirror Node.
          1 NFT = 1 Ticket.
        </p>
      </div>

      
    </div>
  );
};

export default Raffle;
